import { createContext, useContext } from "react";

export type Language = "en" | "pt";
export const LANGUAGE_STORAGE_KEY = "language";

const rawTranslations = {
  en: {
    nav: {
      home: "Home",
      career: "Career",
      contact: "Contact",
      articles: "Articles",
      updates: "Updates",
      projects: "Projects",
      photos: "Photography",
      games: "Games",
      tools: "Tools",
      darkMode: "Switch to dark mode",
      lightMode: "Switch to light mode",
      language: "Language",
      selectLanguage: "Choose your language",
    },
    dialog: {
      title: "Choose your language",
      description: "Select the language you want to use for this website.",
      english: "English",
      portuguese: "Português",
    },
    home: {
      greeting: "Hello",
      message:
        "Here you’ll find my selection of projects, articles, visual explorations, and ideas in development.",
      sliderH2: "Light and Colors",
      sliderMessage:
        "We see only a fraction of the light spectrum. Turn the dial to explore new colors in the UI.",
      explore: "Explore",
    },
    career: {
      title: "Career",
      description:
        "Explore my professional journey with a focus on product strategy, engineering collaboration, and operational excellence in B2B SaaS environments.",
      section1Title: "Design, Product & Collaboration",
      section1p1:
        "Throughout my career, I have consolidated my work at the intersection of product strategy, engineering, and operational excellence, specializing in complex, data-intensive, and highly regulated B2B SaaS ecosystems. My core deliverable is translating highly complex technical challenges into scalable, consistent, and data-driven product experiences.",
      section1p2:
        "My approach prioritizes multidisciplinary collaboration as a lever for impact. I act as the strategic link between Product, Engineering, and Marketing, ensuring that business visions are transformed into well-founded design decisions that are quickly implemented. I advocate for a culture based on autonomy, governance, and operational efficiency; I understand that a team's maturity is reflected in the robustness of its interface architectures and design systems, accelerating time-to-market and reducing technical rework.",
      section1p3:
        "As a technical leader and mentor, I focus on elevating the craft and maturity of the teams. By integrating emerging technology and generative AI as natural extensions of the product strategy, I automate operational flows that reduce the cognitive load on teams, allowing their energy to remain focused on discovering real opportunities. My work ensures that the technical effort of multiple squads is shielded from waste, maintaining agile delivery cadence and protecting the product's market value.",
      section1p4:
        "As a technical leader and mentor, I focus on elevating the craft and maturity of the teams. By integrating emerging technology and generative AI as natural extensions of the product strategy, I automate operational flows that reduce the cognitive load on teams, allowing their energy to remain focused on discovering real opportunities. My work ensures that the technical effort of multiple squads is shielded from waste, maintaining agile delivery cadence and protecting the product's market value.",
      historyTitle: "Background",
      historyP1:
        "My current level of seniority is a direct result of challenges faced in environments that demanded more than design; they demanded solution architecture. My initial foundation at Módulo Security, working with Governance, Risk and Compliance (GRC), shaped my technical rigor and ability to translate regulatory complexities into understandable and secure operational flows. It was in this high-risk and high-responsibility environment that I internalized that corporate product design must be, above all, a seal of trust for the professional user.",
      historyP2:
        "Subsequently, in the transition to Edumobi and Somos Educação, the focus shifted from individual usability to systemic sustainability. Co-founding and expanding Plurall required mastering how to scale an educational product to thousands of users through a coherent ecosystem. There, I moved from working solely at the interface end to leading the structure that allowed multiple squads to design in a standardized way. I developed automation tools and standards that served as the foundation for the platform's continuous evolution, ensuring that user base growth did not result in a degradation of the experience.",
      historyP3:
        "My career path was consolidated by transitioning from execution to the role of craft facilitator. Taking responsibility for raising the technical maturity of the teams meant treating knowledge as an asset to be disseminated through mentoring, documentation, and strategic governance. I see my history as a management asset, where I use the lessons from each transition to anticipate bottlenecks and optimize operational processes. Today, I maintain this integrative vision to ensure that the technical effort of the entire organization is directed towards what really matters: transforming technological complexity into strategic clarity and business impact.",
      recognitionTitle: "Recognition and Impact",
      recognitionHtml:
        "My career in digital product design is marked by recognitions that validate the impact of my work in terms of scale and innovation:<br/><br/><strong>Best Mobile Innovation for Education and Learning (Mobile World Congress - MWC Barcelona 2015)</strong>: At Edumobi, as Principal Designer, I contributed to mobile learning solutions that won global awards from GSMA for transforming learning into accessible experiences via web, SMS, and native applications.<br/><br/><strong>Tela Viva Mobile Award</strong>: The same corporate and educational mobility project was recognized nationally with the Best Education Case award, consolidating the operation as a benchmark in mobile innovation in the country.<br/><br/><strong>Apple Award for Digital Creativity</strong>: During my time at Módulo Security, I participated in the award, reaching the top 5 finalists with a website to promote the work of Doutores da Alegria (Doctors of Joy), a project that used digital creativity to increase the visibility of social actions and demonstrate the power of design in campaigns with human impact.<br/><br/>These awards were not just trophies, but validations of an approach that balances technical innovation with real usability, impacting millions of users in regulated and scalable contexts.",
      recognitionHtml2: "",
      collaborationsTitle: "Moments",
      collaborationsP1:
        "Each milestone in this journey is defined by collective achievements, where engineering, design, and product find their true power through technical collaboration. Below, I share the records and interfaces that materialize these major turning points in my career.",
      skillsTitle: "Skills",
      skillsText:
        "Design systems, product strategy, UI architecture, collaboration, and scalable digital experiences.",
    },
    photos: {
      title: "Photography",
      descrition:
        "Visit the gallery at <a target='_blank' href='https://unsplash.com/@garaujo'>unsplash.com/@garaujo</a> for more details on these and other photos.",
    },
    contact: {
      title: "Contact",
      description:
        "Reach out for opportunities, collaborations, or a friendly conversation about design and product.",
      heading: "Get in touch",
      professional:
        "For professional inquiries like opportunities, partnerships, or design/product collaborations, connect with me on LinkedIn: ",
      personal:
        "For personal messages, feedback, or just to say hello, feel free to reach out by email: ",
      linkedIn: "linkedin.com/in/guilhermeasouza",
      email: "garaujo.work@gmail.com",
    },
    projectPage: {
      riskManagerTitle: "Risk Manager",
      riskManagerIntroP1:
        "My journey with Risk Manager began at Módulo when the product was still in its early stages as the Checkup Tool. I worked directly on the conception, interface design, and evolution of this enterprise Governance, Risk, and Compliance (GRC) solution. Collaborating closely with multidisciplinary teams, I translated critical approval, audit, and incident workflows into intuitive user journeys, playing a key role in transforming the tool into what would become Risk Manager.",
      riskManagerIntroP2:
        "As the product matured and made its strategic transition into a B2B SaaS platform, I scaled my impact to ensure the system could support highly complex, regulated environments. I became the bridge between design and front-end engineering, optimizing code (HTML, CSS, and JavaScript) to guarantee performance, scalability, and technical quality across agile, high-impact deliveries—including solutions deployed at Rio+20 and major projects for TIM Brasil.",
      caseTitle: "Plurall",
      caseIntroP1:
        "My journey with Plurall began at Edumobi during its startup phase, where I led the initial design in close collaboration with the founders and operational teams. I participated in product and experience decisions alongside C-level executives while aligning deliveries with engineering and marketing to keep momentum.",
      caseIntroP2:
        "After the acquisition by SOMOS Educação, I scaled this approach across complex multi-squad teams as Design System Ops in the Design chapter. I became a reference for technical leads, product managers, and executives, driving ongoing alignment on technical feasibility, strategic vision, and visual identity amid challenging integrations.",
      caseSectionTitle: "Uniting cultures at scale",
      caseSectionBody1:
        "The greatest challenge was integrating cultures and tools from multiple post-acquisition companies to turn Plurall into a complete ecosystem. As a co-founder of the Plurall Design System focused on governance, I led documentation creation, component QA, and workshops with designers, engineers, and ops leaders to ensure parity between design and code.",
      caseSectionBody2:
        "This structure supported growth for millions of users. I built Figma automations and optimized handoffs that reduced rework and increased visual consistency at national scale. My work influenced product roadmaps while supporting advanced educational solutions and executive engagement reporting.",
      purposeTitle: "Email solution",
      purposeText1:
        "The growth team asked for email templates for SendGrid, which requires specific HTML formatting for maximum compatibility. It became clear that without technical intervention on each send, the content would break, requiring constant review and slowing the team down.",
      purposeText2:
        "Instead of delivering static files, I developed String Stacker: an internal tool that let the team compose emails visually while generating production-ready HTML aligned to the Design System.",
      purposeText3:
        'The result was faster delivery, zero technical rework, and greater confidence to experiment with growth approaches. This experience reinforced the principle that understanding the "why" behind a request reveals much larger opportunities for impact. With autonomy and the trust of leaders, I turned a one-off demand into a scalable solution for multiple squads.',
      visitPlurall: "Visit Plurall",
      casePlurall: "CASE Plurall",
      readArticle: "Read the article",
      eloCopilotTitle: "Figma Plugins and Fonts",
      eloCopilotDescription:
        "I developed a plugin focused on operational efficiency that automates the creation of complex interface flows. The tool stands out by delivering components with auto-layout and pre-configured design patterns, removing repetitive alignment work and ensuring a technically standardized workflow. In addition, based on an open-source project, I built a custom plugin for managing Design System icons, taking technical ownership of maintaining and versioning the TrueType font file (TTF).",
      otherProjectsTitle: "Other projects",
      linkedinCaseTitle: "Case Study: LinkedIn as a Tool",
      linkedinCaseBody:
        "A holistic study on LinkedIn, based on analyzed visitation data, personal user pain points, and daily insights from headhunters, exploring how it could function as a practical and efficient tool with repositioned features, while still maintaining its core purpose as a social network for facilitating connections between people.",
      linkedinCaseButton: "View on Behance",
    },
    plurallPage: {
      headerTitle: "Plurall & Elo Design System",
      heroTag: "Product Design · Design Systems · Design Engineering",
      heroTitle: "From post-class tool to large-scale educational platform",
      heroLead:
        "Plurall started as a focused product inside Edumobi. Over ten years it became a flagship K-12 platform, survived acquisitions, absorbed capabilities from other companies and required a Design System capable of supporting scale, consistency and multi-squad collaboration.",
      caseAuthorBy: "Case described by",
      meta: {
        rolesLabel: "Roles",
        rolesItems: [
          "Principal Designer",
          "Staff Design Engineer",
          "Design System Ops",
        ],
        companiesLabel: "Companies",
        companiesItems: ["Edumobi", "Somos Educação", "Cogna Educação"],
        scopesLabel: "Scopes",
        scopesItems: ["Product", "Design Systems", "Governance"],
        environmentsLabel: "Environments",
        environmentsItems: [
          "K-12 / B2B SaaS",
          "Startup & Corporate",
          "End-to-end & Multi-squad",
        ],
      },
      origin: {
        eyebrow: "01 — Origin",
        title: "Building the Foundations of Plurall",
        p1: "In 2013, inside the startup Edumobi (Abril Educação group), Plurall was created as a post-class support tool. The goal was clear: help students follow their learning, give visibility to parents and deliver actionable metrics to school teams.",
        p2: "I joined as Principal Designer, working directly with the founders, technology team and supporting marketing. The product launched at Colégio PH in Rio de Janeiro. Very quickly, the potential for scale became evident.",
      },
      whiteLabel: {
        eyebrow: "02 — First scaling challenge",
        title: "White-label architecture",
        p1: "When Plurall expanded to other schools in the group, it needed to serve multiple brands. The product had not been designed for that. The challenge was to deliver the same base experience while allowing each brand its own visual identity, not only logos, but colors, typography and interface elements.",
        p2: "The solution was structural. We built a layered styling system based on subdomains. Each brand pointed to its own style set, inherited the common base and overrode only what was necessary. No !important, no chaotic class stacking. The product architecture stayed deliberately simple: a main dashboard of recent interactions, a content area and a doubt-resolution interface. That simplicity made style control and journey clarity sustainable.",
        note: "Plurall became white-label while still at Edumobi, before any acquisition.",
      },
      platformShift: {
        eyebrow: "03 — Context shift",
        title: "From white-label product to unified platform",
        p1: "Abril Educação became Somos Educação. The product kept growing. In 2018, Somos was acquired by Kroton (which rebranded to Cogna in 2019). At that point Plurall stopped being a white-label product and was repositioned as a unified educational platform.",
        p2: "The visual identity was rebuilt. The product adopted a single identity and stopped carrying brand variations. At the same time, solutions from different acquired companies, school management, learning materials, online assessments and other fronts, were incorporated into the platform.",
        p3: "What had been a relatively controlled product became a multi-product, multi-squad, multi-culture design environment.",
      },
      fragmentation: {
        eyebrow: "04 — The real problem",
        title: "Identity change and cultural fragmentation",
        p1: "The identity shift and the transformation into a platform created a concrete risk: visual fragmentation, inconsistent patterns and loss of operational efficiency. Teams coming from different companies and design cultures needed to work on the same experience. The visual language had to become unique, scalable and governable.",
        p2: "This became the main driver for building the Design System. It was not only about organizing components. It was about creating the foundation that would sustain the new platform identity and allow dozens of designers and developers to work aligned, without rework and without loss of quality.",
      },
      eloSystem: {
        eyebrow: "05 — Design System Elo",
        title: "Co-founding and leading the system",
        p1: "I co-founded and led the evolution of the Elo Design System. We defined vision, mission, foundations, tokens, component architecture, patterns and templates. During this stage, alignment with a11y and WCAG was validated. We established parity between the design library and the production codebase in direct partnership with engineering.",
        p2: "I acted as a transversal UX/UI reference, supporting multiple squads on experience decisions, flow reviews and visual refinement. The hybrid role was intentional: the system would only scale if its principles existed both inside the design tool and inside the product itself.",
      },
      libraryToSystem: {
        eyebrow: "06 — Information architecture",
        title: "From a library to a system",
        p1: "The system was structured in layers so that decisions could be progressively reused. Instead of organizing it only as a catalogue of components, the architecture reflected how teams actually built product experiences.",
      },
      layers: {
        foundations: {
          name: "Foundations",
          description:
            "Typography, color, spacing, iconography, layout and visual properties.",
        },
        tokens: {
          name: "Tokens",
          description:
            "Shared values connecting design decisions to implementation.",
        },
        components: {
          name: "Components",
          description:
            "Reusable interface primitives with defined behavior and states.",
        },
        patterns: {
          name: "Patterns",
          description:
            "Combinations of components solving recurring product problems.",
        },
        experiences: {
          name: "Experiences",
          description:
            "Product experiences assembled from shared system decisions.",
        },
        governance: {
          name: "Governance",
          description:
            "The mechanism connecting usage, implementation and evolution.",
        },
      },
      designPrinciple: {
        eyebrow: "07 — Design principle",
        title: "A Design System should reduce decisions, not create them",
        p1: "A common failure mode of Design Systems is becoming a large catalogue of components that still requires designers to make most of the important decisions themselves.",
        p2: "The approach here was different: if a problem appeared repeatedly, we asked whether that decision could become part of the system.",
        highlight:
          "Every repeated decision was a potential opportunity for infrastructure.",
      },
      designOps: {
        eyebrow: "08 — Design Ops & governance",
        title: "Structure without becoming a bottleneck",
        p1: "Governance was designed in practice. We created onboarding processes, usage playbooks, design chapter rituals and semantic versioning of components. Updates went through critiques: the designer identified the need, we refined the proposal together and, when viable, it entered the creation or evolution flow.",
        p2: "I participated actively in Git, reviewing and approving components, and later aligned the work in a distributed way with developers from each squad. The goal was enough structure to preserve consistency while allowing squads to remain autonomous.",
      },
      automation: {
        eyebrow: "09 — Automation",
        title: "The system became a tool",
        p1: "As the operation grew, repeated frictions became visible. I built custom Figma plugins that automated screen generation, giving designers a starting point already aligned with the Design System. The plugin incorporated flows, breakpoints, existing system components and Auto Layout.",
      },
      iconography: {
        eyebrow: "10 — Iconography",
        title: "A small problem became a system problem",
        p1: "Before adequate native solutions existed in Figma, I designed an icon font architecture that worked across both design and production. A search plugin allowed designers to find and insert icons directly into components. Icons were exported properly for the mobile versions, and the same font was used by both design and web development, unifying design and implementation and simplifying iconography maintenance across teams.",
      },
      designEngineering: {
        eyebrow: "11 — Design ↔ Engineering",
        title: "One system, two representations",
        p1: "A central principle was maintaining parity between the design library and production implementation. Tokens, components and shared patterns became the bridge between these two representations.",
      },
      observability: {
        eyebrow: "12 — Observability",
        title: "You can't improve what you can't see",
        p1: "As Figma administrator, I built a tool to extract real-time component usage from projects and generate CSV reports for tech leads. This created an additional layer of visibility: which components were being used, where adoption existed and where potential gaps or inconsistencies could be identified.",
      },
      outcome: {
        eyebrow: "13 — Outcome",
        title: "Infrastructure, not just a library",
        p1: "The transition from a white-label model to a platform with a single identity was sustained by the Design System, which became the source of truth for the visual and interface experience. Consistency held at scale even as solutions from different companies and cultures were absorbed. Rework dropped. Alignment across teams increased. Parity between design and code became real, not aspirational.",
        p2: "The Design System stopped being a component repository. It was treated as an internal product, with users, governance and a continuous evolution cycle. That model became an operational reference inside the ecosystem.",
      },
      outcomes: {
        foundations: {
          name: "Foundations",
          description: "A shared visual language for the platform.",
        },
        tokens: {
          name: "Tokens",
          description: "A bridge between design decisions and implementation.",
        },
        componentsPatterns: {
          name: "Components & patterns",
          description: "Reusable product building blocks.",
        },
        governance: {
          name: "Governance",
          description:
            "A model for maintaining consistency as the system evolved.",
        },
        automation: {
          name: "Automation",
          description:
            "Tools that converted repeated design work into reusable workflows.",
        },
        observability: {
          name: "Observability",
          description: "Usage data supporting system evolution.",
        },
      },
      recognition: {
        eyebrow: "14 — Recognition",
        title: "Early product recognition",
        p1: "During the Edumobi years, the product and the design work around it contributed to industry recognition, including Melhor Case de Educação (Prêmio Tela Viva Móvel).",
      },
      impact: {
        eyebrow: "15 — Impact",
        title: "The most important outcome was organizational",
        p1: "The project changed what the design team could scale. Instead of scaling by adding more people to repeat the same decisions, the organization could scale by turning knowledge into infrastructure.",
        highlight:
          "Knowledge that previously lived in people became part of the product infrastructure.",
      },
      learning: {
        eyebrow: "16 — Learning",
        title: "Design Systems are products",
        p1: "The most important lesson was that a Design System is not primarily a collection of UI components. It has users, adoption problems, technical constraints, governance, debt and evolution.",
        question: "What repeated decision can become infrastructure?",
        p2: "That question led this project from components to automation, from automation to governance, and from governance to observability.",
      },
      closing: {
        tag: "The thesis",
        title:
          "Complex systems don't need less complexity. They need better structure.",
        p1: "By connecting Design, Engineering, Product and automation, the Elo Design System transformed accumulated design knowledge into reusable infrastructure that supported Plurall from a focused product to a large-scale educational platform.",
      },
      diagrams: {
        architecture: {
          ariaLabel: "Design System architecture",
          productExperiences: "PRODUCT EXPERIENCES",
          patterns: "PATTERNS",
          components: "COMPONENTS",
          tokens: "TOKENS",
          foundations: "FOUNDATIONS",
          governance: "GOVERNANCE",
          feedback: "FEEDBACK",
        },
        automationFlow: {
          ariaLabel: "Figma Plugin Automation Flow",
          pluginTitle: "FIGMA PLUGIN",
          feature1: "Uses DS Components",
          feature2: "Configures Auto Layout",
          feature3: "Organizes the Canvas",
          inputTitle1: "INPUT",
          inputTitle2: "(DESIGNER)",
          inputItem1: "Flow / Context",
          inputItem2: "Breakpoint",
          outputTitle: "OUTPUT",
          outputItem1: "Editable UI",
          outputItem2: "System-Aligned",
          rulesTitle: "SYSTEM RULES",
          rulesItem1: "Tokens & Rules",
        },
        designCode: {
          title: "Design and engineering parity",
          description:
            "Design and engineering share foundations, tokens and components through a common system.",
          design: "DESIGN",
          engineering: "ENGINEERING",
          designItems: ["Figma", "Components", "Tokens", "Patterns"],
          engineeringItems: ["Production", "Components", "Tokens", "UI"],
          sharedSystem: "SHARED SYSTEM",
          parity: "PARITY",
        },
        governanceLoop: {
          title: "Governance and observability loop",
          description:
            "A continuous loop connecting usage data, governance, system evolution and product teams.",
          designSystemTop: "DESIGN",
          designSystemBottom: "SYSTEM",
          usageData: "USAGE DATA",
          governance: "GOVERNANCE",
          evolution: "EVOLUTION",
          productTeams: "PRODUCT TEAMS",
        },
      },
      gallery: {
        screensAlt1: "Plurall product interface",
        screensAlt2: "Plurall product interfaces",
        eloCopilotPluginAlt: "Elo Copilot Figma plugin",
        eloCopilotGenerationAlt: "Elo Copilot screen generation",
        eloIconsPluginAlt: "Plugin Elo Icons",
      },
    },
    articlePage: {
      notFoundTitle: "Article not found",
      notFoundText:
        "This article could not be found. Please check the articles list.",
      backToArticles: "Back to articles",
      sourceLabel: "Source",
      continueReading: "Continue reading the article",
    },
    tools: {
      nav: "Tools",
      qrCode: "QR Code",
      qrCodeDesc: "Generate QR codes and export as PNG or SVG.",
      password: "Password Generator",
      passwordDesc: "Generate secure random passwords with customizable options.",
      qrCodePage: {
        title: "QR Code",
        valueLabel: "Value",
        valuePlaceholder: "Enter text or URL",
        emptyMessage: "Enter a value to generate a QR code",
        sizeSmall: "Small (320w)",
        sizeMedium: "Medium (640w)",
        sizeLarge: "Large (960w)",
        agentStep1: "Enter the text or URL to encode in the QR code field.",
        agentStep2: "The QR code is generated in real-time as you type.",
        agentStep3: "Use the PNG or SVG export buttons to download at the desired resolution.",
      },
      passwordPage: {
        title: "Password Generator",
        lengthLabel: "Character length",
        letters: "Letters (a-z)",
        numbers: "Numbers (0-9)",
        specialChars: "Special characters",
        mixedCase: "Mixed case (A-Z)",
        generate: "Generate",
        copy: "Copy",
        copied: "Copied!",
        emptyMessage: "Click generate to create a password",
        agentStep1: "Adjust the character length slider (8-64 in steps of 8).",
        agentStep2: "Select character types: letters, numbers, special characters, and mixed case.",
        agentStep3: "Click Generate to create the password, then Copy to clipboard.",
      },
      agentInfo: {
        title: "For AI Agents",
        description: "Navigate to this tool to generate output. No API key or endpoint required.",
        descriptionLabel: "What it does",
        howToUse: "How to use",
        copy: "Copy URL",
        copied: "Copied!",
      },
    },
    articlesPage: {
      description: "Collection of essays and technical write-ups from the lab.",
      featuredLabel: "Read featured article",
      readLabel: "Read article",
      notePrefix:
        "Most of the content in this list was originally published on other sites such as",
      noteAnd: "and",
      noteSubstack: "AI Front (Substack)",
      noteMedium: "Medium",
    },
    updatesPage: {
      title: "Updates",
      description:
        "A central place for direct site news, X posts, and Reddit highlights.",
      siteUpdatesTitle: "Latest site updates",
      xFeedTitle: "X feed",
      redditFeedTitle: "Reddit highlights",
      readMore: "Read more",
      viewOnX: "View on X",
      viewOnReddit: "View on Reddit",
      feedNote:
        "External feeds are shown here as a snapshot. For full discussion, follow the original links.",
    },
    previous: "Previous",
    next: "Next",
    footer: {
      copyright: "© {year} Guilherme Araujo. All rights reserved.",
    },
  },
  pt: {
    nav: {
      home: "Início",
      career: "Carreira",
      contact: "Contato",
      articles: "Artigos",
      updates: "Atualizações",
      photos: "Fotografia",
      projects: "Projetos",
      games: "Jogos",
      tools: "Ferramentas",
      darkMode: "Ativar modo escuro",
      lightMode: "Ativar modo claro",
      language: "Idioma",
      selectLanguage: "Escolha seu idioma",
    },
    dialog: {
      title: "Escolha seu idioma",
      description: "Selecione o idioma que deseja usar neste site.",
      english: "English",
      portuguese: "Português",
    },
    home: {
      greeting: "Olá",
      message:
        "Aqui você encontrará minha seleção de projetos, artigos, explorações visuais e ideias em desenvolvimento.",
      sliderH2: "Luz e Cores",
      sliderMessage:
        "Vemos apenas uma fração do espectro de luz. Gire o dial para explorar novas cores na UI.",
      explore: "Explorar",
    },
    career: {
      title: "Carreira",
      description:
        "Explore minha trajetória profissional com foco em estratégia de produto, colaboração com engenharia e excelência operacional em ambientes B2B SaaS.",
      section1Title: "Design, Produto & Colaboração",
      section1p1:
        "Ao longo da minha trajetória, consolidei minha atuação na interseção entre estratégia de produto, engenharia e excelência operacional, especializando-me em ecossistemas B2B SaaS complexos, data-intensive e altamente regulados. Minha entrega central está em traduzir desafios técnicos de alta complexidade em experiências de produto escaláveis, consistentes e orientadas a dados.",
      section1p2:
        "Minha abordagem prioriza a colaboração multidisciplinar como alavanca de impacto. Atuo como o elo estratégico entre Produto, Engenharia e Marketing, garantindo que as visões de negócio se transformem em decisões de design fundamentadas e de rápida implementação. Defendo uma cultura baseada em autonomia, governança e eficiência operacional; entendo que a maturidade de um time se reflete na robustez de suas arquiteturas de interface e design systems, acelerando o time-to-market e reduzindo o retrabalho técnico.",
      section1p3:
        "Como liderança técnica e mentor, foco em elevar o craft e a maturidade das equipes. Ao integrar tecnologia emergente e IA generativa como extensões naturais da estratégia de produto, automatizo fluxos operacionais que reduzem a carga cognitiva dos times, permitindo que a energia permaneça na descoberta de oportunidades reais. Minha atuação garante que o esforço técnico de múltiplos squads esteja blindado contra o desperdício, mantendo a cadência de entrega ágil e protegendo o valor do produto no mercado.",
      section1p4:
        "Como liderança técnica e mentor, foco em elevar o craft e a maturidade das equipes. Ao integrar tecnologia emergente e IA generativa como extensões naturais da estratégia de produto, automatizo fluxos operacionais que reduzem a carga cognitiva dos times, permitindo que a energia permaneça na descoberta de oportunidades reais. Minha atuação garante que o esforço técnico de múltiplos squads esteja blindado contra o desperdício, mantendo a cadência de entrega ágil e protegendo o valor do produto no mercado.",
      historyTitle: "Histórico",
      historyP1:
        "A construção da minha senioridade atual é o resultado direto de desafios enfrentados em ambientes que exigiam mais do que design, exigiam arquitetura de solução. Minha base inicial na Módulo Security, trabalhando com Governance, Risk and Compliance (GRC), moldou meu rigor técnico e a capacidade de traduzir complexidades normativas em fluxos operacionais compreensíveis e seguros. Foi nesse cenário de alto risco e responsabilidade que internalizei que o design de produto corporativo deve ser, antes de tudo, um selo de confiança para o usuário profissional.",
      historyP2:
        "Posteriormente, na transição para a Edumobi e Somos Educação, o foco migrou da usabilidade individual para a sustentabilidade sistêmica. A co-fundação e expansão do Plurall exigiram o domínio de como escalar um produto educacional para milhares de usuários através de um ecossistema coerente. Ali, deixei de atuar apenas na ponta da interface para liderar a estrutura que permitia a múltiplas squads desenhar de forma padronizada. Desenvolvi ferramentas de automação e padrões que serviram como alicerce para a evolução contínua da plataforma, garantindo que o crescimento da base de usuários não resultasse em degradação da experiência.",
      historyP3:
        "Minha trajetória consolidou-se pela transição da execução para o papel de facilitador de craft. Assumir a responsabilidade de elevar a maturidade técnica dos times significou tratar o conhecimento como um ativo a ser disseminado por meio de mentorias, documentação e governança estratégica. Enxergo meu histórico como um ativo de gestão, onde utilizo as lições de cada transição para antecipar gargalos e otimizar processos operacionais. Hoje, mantenho essa visão integradora para garantir que o esforço técnico de toda a organização seja direcionado ao que realmente importa: transformar complexidade tecnológica em clareza estratégica e impacto de negócio.",
      recognitionTitle: "Reconhecimentos e Impacto",
      recognitionHtml:
        "Minha trajetória em design de produtos digitais é marcada por reconhecimentos que validam o impacto do meu trabalho em escala e inovação:<br/><br/>Apple Award for Digital Creativity: Durante meu período na Módulo Security, participei da premiação chegando entre os 5 finalistas com um hotsite para divulgar o trabalho dos Doutores da Alegria, um projeto que utilizou criatividade digital para ampliar a visibilidade de ações sociais e demonstrar o poder do design em campanhas de impacto humano.<br/><br/>Best Mobile Innovation for Education and Learning (Mobile World Congress - MWC Barcelona 2015): Na Edumobi, como Principal Designer, contribuí para soluções de mobile learning premiadas globalmente pela GSMA por transformar o aprendizado em experiências acessíveis via web, SMS e aplicativos nativos.<br/><br/>Tela Viva Mobile Award: O mesmo projeto de mobilidade corporativa e educacional foi reconhecido nacionalmente com o prêmio de Melhor Case de Educação, consolidando a operação como referência em inovação móvel no país.<br/><br/>Esses prêmios não foram apenas troféus, mas validações de uma abordagem que equilibra inovação técnica com usabilidade real, impactando milhões de usuários em contextos regulados e escaláveis.",
      recognitionHtml2: "",
      collaborationsTitle: "Momentos",
      collaborationsP1:
        "Cada marco nesta jornada é definido por realizações coletivas, onde a engenharia, o design e o produto encontram sua verdadeira potência através da colaboração técnica. A seguir, compartilho os registros e as interfaces que materializam essas grandes viradas de chave na minha carreira.",
      skillsTitle: "Habilidades",
      skillsText:
        "Sistemas de design, estratégia de produto, arquitetura de UI, colaboração e experiências digitais escaláveis.",
    },
    photos: {
      title: "Fotografia",
      descrition:
        "Acesse a galeria no <a target='_blank' href='https://unsplash.com/@garaujo'>unsplash.com/@garaujo</a> para maiores detalhes dessas e outras fotos.",
    },
    contact: {
      title: "Contato",
      description:
        "Entre em contato para oportunidades, colaborações ou uma conversa sobre design e produto.",
      heading: "Vamos conversar",
      professional:
        "Para assuntos profissionais, como oportunidades, parcerias ou colaborações em design/produto, conecte-se comigo no LinkedIn: ",
      personal:
        "Para mensagens pessoais, como feedback, perguntas ou apenas para dizer olá, sinta-se à vontade para me enviar um e-mail: ",
      linkedIn: "linkedin.com/in/guilhermeasouza",
      email: "garaujo.work@gmail.com",
    },
    projectPage: {
      riskManagerTitle: "Risk Manager",
      riskManagerIntroP1:
        "Minha jornada com o Risk Manager começou na Módulo quando o produto ainda dava seus primeiros passos como Checkup Tool. Atuei diretamente na concepção, design de interface e evolução dessa solução enterprise de Governança, Risco e Compliance (GRC). Colaborei de perto com times multidisciplinares para traduzir workflows críticos de approval, audit e incident em jornadas intuitivas, participando ativamente da transformação da ferramenta no que viria a ser o Risk Manager.",
      riskManagerIntroP2:
        "Com o amadurecimento do produto e sua transição estratégica para o modelo B2B SaaS, escalei minha atuação para garantir que a plataforma suportasse ambientes regulados de altíssima complexidade. Tornei-me a ponte entre o design e a engenharia de front-end, otimizando código (HTML, CSS e JavaScript) para garantir performance, escalabilidade e qualidade técnica em entregas ágeis de grande impacto — incluindo soluções utilizadas na Rio+20 e em projetos para a TIM Brasil.",
      caseTitle: "Plurall",
      caseIntroP1:
        "Minha jornada com o Plurall começou na Edumobi, ainda em fase de startup, onde liderei o design inicial em colaboração direta com os fundadores e times operacionais. Participava de decisões estratégicas de produto e experiência ao lado de C-levels, enquanto alinhava entregas com engenharia e marketing para manter agilidade.",
      caseIntroP2:
        "Com a incorporação pela SOMOS Educação, escalei essa abordagem para squads complexos como Design System Ops no capítulo de Design. Tornei-me referência para líderes técnicos, de produto e executivos, alinhando continuamente visão estratégica, viabilidade técnica e identidade visual em meio a integrações desafiadoras.",
      caseSectionTitle: "Unificando culturas em escala",
      caseSectionBody1:
        "O maior obstáculo foi integrar culturas e ferramentas de múltiplas empresas pós-aquisição para transformar o Plurall em um ecossistema completo. Como co-fundador do Design System do Plurall focado em governança, liderei a criação de documentação, QA de componentes e workshops com designers, desenvolvedores e líderes operacionais para garantir paridade entre design e código.",
      caseSectionBody2:
        "Essa estrutura sustentou o crescimento para milhões de usuários. Criei automações em Figma e handoffs otimizados que reduziram retrabalho e elevaram a consistência visual em escala nacional. Minha atuação influenciou roadmaps de produto enquanto apoio soluções pedagógicas avançadas e relatórios executivos de engajamento.",
      purposeTitle: "Email solution",
      purposeText1:
        "O pedido do time de growth foi templates de e-mail para SendGrid, que exige HTML formatado específico para máxima compatibilidade. Ficou claro que, sem intervenção técnica em cada envio, o conteúdo viria quebrado, exigindo revisão constante e travando a agilidade do time.",
      purposeText2:
        "Em vez de entregar arquivos estáticos, desenvolvi o String Stacker: uma ferramenta interna que permitiu ao time montar e-mails visualmente enquanto gerava HTML pronto para produção e alinhado ao Design System.",
      purposeText3:
        'O resultado foi entregas mais rápidas, zero retrabalho técnico e maior confiança para experimentar novas abordagens de growth. Essa experiência reforça o princípio de que entender o "porquê" por trás de uma demanda revela oportunidades de impacto muito maiores.',
      visitPlurall: "Visitar Plurall",
      casePlurall: "CASE Plurall",
      readArticle: "Ler o artigo",
      eloCopilotTitle: "Figma Plugins e Fontes",
      eloCopilotDescription:
        "Desenvolvi um plugin focado em eficiência operacional que automatiza a geração de fluxos de interface complexos. O diferencial da ferramenta é a entrega de componentes com Auto-layout e padrões de projeto pré-configurados, eliminando tarefas repetitivas de alinhamento e garantindo um workflow tecnicamente padronizado. Além disso, a partir de um projeto open source, construí um plugin customizado para a gestão de ícones do Design System, assumindo a responsabilidade técnica pela manutenção e versionamento do arquivo de fontes TrueType (TTF).",
      otherProjectsTitle: "Outros projetos",
      linkedinCaseTitle: "Case Study: LinkedIn como Ferramenta",
      linkedinCaseBody:
        "Um estudo holístico sobre o LinkedIn, baseado em dados analisados de visitação, pontos de dor pessoais de usuários e insights diários de recrutadores, explorando como a plataforma poderia funcionar como uma ferramenta prática e eficiente com funcionalidades reposicionadas, mantendo ainda seu propósito central como rede social para facilitar conexões entre pessoas.",
      linkedinCaseButton: "Ver no Behance",
    },
    plurallPage: {
      headerTitle: "Plurall & Elo Design System",
      heroTag: "Product Design · Design Systems · Design Engineering",
      heroTitle: "De ferramenta pós-aula a plataforma educacional em larga escala",
      heroLead:
        "O Plurall começou como um produto focado dentro da Edumobi. Ao longo de dez anos, tornou-se uma plataforma K-12 de destaque, sobreviveu a aquisições, absorveu capacidades de outras empresas e exigiu um Design System capaz de sustentar escala, consistência e colaboração entre múltiplos squads.",
      caseAuthorBy: "Case descrito por",
      meta: {
        rolesLabel: "Papéis",
        rolesItems: [
          "Principal Designer",
          "Staff Design Engineer",
          "Design System Ops",
        ],
        companiesLabel: "Empresas",
        companiesItems: ["Edumobi", "Somos Educação", "Cogna Educação"],
        scopesLabel: "Escopos",
        scopesItems: ["Produto", "Design Systems", "Governança"],
        environmentsLabel: "Ambientes",
        environmentsItems: [
          "K-12 / B2B SaaS",
          "Startup & Corporativo",
          "End-to-end & Multi-squad",
        ],
      },
      origin: {
        eyebrow: "01 — Origem",
        title: "Construindo as fundações do Plurall",
        p1: "Em 2013, dentro da startup Edumobi (grupo Abril Educação), o Plurall foi criado como uma ferramenta de apoio pós-aula. O objetivo era claro: ajudar estudantes a acompanhar o aprendizado, dar visibilidade aos pais e entregar métricas acionáveis para os times das escolas.",
        p2: "Entrei como Principal Designer, trabalhando diretamente com os fundadores, o time de tecnologia e apoiando o marketing. O produto foi lançado no Colégio PH, no Rio de Janeiro. Muito rapidamente, o potencial de escala ficou evidente.",
      },
      whiteLabel: {
        eyebrow: "02 — Primeiro desafio de escala",
        title: "Arquitetura white-label",
        p1: "Quando o Plurall expandiu para outras escolas do grupo, precisou atender múltiplas marcas. O produto não havia sido desenhado para isso. O desafio era entregar a mesma experiência base permitindo que cada marca tivesse sua própria identidade visual, não apenas logos, mas cores, tipografia e elementos de interface.",
        p2: "A solução foi estrutural. Construímos um sistema de estilos em camadas baseado em subdomínios. Cada marca apontava para seu próprio conjunto de estilos, herdava a base comum e sobrescrevia apenas o necessário. Sem !important, sem empilhamento caótico de classes. A arquitetura do produto permaneceu deliberadamente simples: um dashboard principal de interações recentes, uma área de conteúdo e uma interface de resolução de dúvidas. Essa simplicidade tornou sustentáveis o controle de estilos e a clareza das jornadas.",
        note: "O Plurall se tornou white-label ainda na Edumobi, antes de qualquer aquisição.",
      },
      platformShift: {
        eyebrow: "03 — Mudança de contexto",
        title: "De produto white-label a plataforma unificada",
        p1: "A Abril Educação virou Somos Educação. O produto continuou crescendo. Em 2018, a Somos foi adquirida pela Kroton (que passou a se chamar Cogna em 2019). Nesse ponto, o Plurall deixou de ser um produto white-label e foi reposicionado como uma plataforma educacional unificada.",
        p2: "A identidade visual foi reconstruída. O produto adotou uma identidade única e deixou de carregar variações de marca. Ao mesmo tempo, soluções de diferentes empresas adquiridas, gestão escolar, materiais de aprendizagem, avaliações online e outras frentes foram incorporadas à plataforma.",
        p3: "O que era um produto relativamente controlado virou um ambiente de design multi-produto, multi-squad e multi-cultura.",
      },
      fragmentation: {
        eyebrow: "04 — O problema real",
        title: "Mudança de identidade e fragmentação cultural",
        p1: "A mudança de identidade e a transformação em plataforma criaram um risco concreto: fragmentação visual, padrões inconsistentes e perda de eficiência operacional. Times vindos de empresas e culturas de design diferentes precisavam trabalhar na mesma experiência. A linguagem visual precisava se tornar única, escalável e governável.",
        p2: "Isso se tornou o principal motor para construir o Design System. Não se tratava apenas de organizar componentes. Era sobre criar a fundação que sustentaria a nova identidade da plataforma e permitiria que dezenas de designers e desenvolvedores trabalhassem alinhados, sem retrabalho e sem perda de qualidade.",
      },
      eloSystem: {
        eyebrow: "05 — Design System Elo",
        title: "Co-fundação e liderança do sistema",
        p1: "Co-fundei e liderei a evolução do Elo Design System. Definimos visão, missão, foundations, tokens, arquitetura de componentes, padrões e templates. Nessa etapa, foi validado o alinhamento com a11y e WCAG. Estabelecemos paridade entre a biblioteca de design e o código de produção em parceria direta com a engenharia.",
        p2: "Atuei como referência transversal de UX/UI, apoiando múltiplos squads em decisões de experiência, revisões de fluxo e refinamento visual. O papel híbrido foi intencional: o sistema só escalaria se seus princípios existissem tanto dentro da ferramenta de design quanto dentro do próprio produto.",
      },
      libraryToSystem: {
        eyebrow: "06 — Arquitetura de informação",
        title: "De biblioteca a sistema",
        p1: "O sistema foi estruturado em camadas para que decisões pudessem ser progressivamente reutilizadas. Em vez de organizá-lo apenas como um catálogo de componentes, a arquitetura refletia como os times realmente construíam experiências de produto.",
      },
      layers: {
        foundations: {
          name: "Foundations",
          description:
            "Tipografia, cor, espaçamento, iconografia, layout e propriedades visuais.",
        },
        tokens: {
          name: "Tokens",
          description:
            "Valores compartilhados que conectam decisões de design à implementação.",
        },
        components: {
          name: "Componentes",
          description:
            "Primitivas de interface reutilizáveis com comportamento e estados definidos.",
        },
        patterns: {
          name: "Padrões",
          description:
            "Combinações de componentes que resolvem problemas recorrentes de produto.",
        },
        experiences: {
          name: "Experiências",
          description:
            "Experiências de produto construídas a partir de decisões compartilhadas do sistema.",
        },
        governance: {
          name: "Governança",
          description: "O mecanismo que conecta uso, implementação e evolução.",
        },
      },
      designPrinciple: {
        eyebrow: "07 — Princípio de design",
        title: "Um Design System deve reduzir decisões, não criá-las",
        p1: "Um modo comum de falha dos Design Systems é virar um grande catálogo de componentes que ainda exige que os designers tomem sozinhos a maior parte das decisões importantes.",
        p2: "A abordagem aqui foi diferente: quando um problema aparecia repetidamente, questionávamos se aquela decisão poderia se tornar parte do sistema.",
        highlight:
          "Cada decisão repetida era uma oportunidade em potencial de infraestrutura.",
      },
      designOps: {
        eyebrow: "08 — Design Ops & governança",
        title: "Estrutura sem virar gargalo",
        p1: "A governança foi desenhada na prática. Criamos processos de onboarding, playbooks de uso, rituais de chapter de design e versionamento semântico de componentes. Atualizações passavam por críticas: o designer identificava a necessidade, refinávamos a proposta juntos e, quando viável, ela entrava no fluxo de criação ou evolução.",
        p2: "Participei ativamente do Git, revisando e aprovando componentes, e depois alinhei o trabalho de forma distribuída com desenvolvedores de cada squad. O objetivo era estrutura suficiente para preservar a consistência mantendo os squads autônomos.",
      },
      automation: {
        eyebrow: "09 — Automação",
        title: "O sistema virou ferramenta",
        p1: "Conforme a operação crescia, fricções repetidas ficaram visíveis. Construí plugins customizados de Figma que automatizavam a geração de telas, dando aos designers um ponto de partida já alinhado ao Design System. O plugin incorporava fluxos, breakpoints, componentes existentes do sistema e Auto Layout.",
      },
      iconography: {
        eyebrow: "10 — Iconografia",
        title: "Um problema pequeno virou problema de sistema",
        p1: "Antes que soluções nativas adequadas existissem no Figma, desenhei uma arquitetura de icon font que funcionava tanto no design quanto na produção. Um plugin de busca permitia que designers encontrassem e inserissem ícones diretamente nos componentes. Os ícones eram exportados adequadamente para as versões mobile, e a mesma fonte era usada pelo design e pelo desenvolvimento web, unificando design e implementação e simplificando a manutenção da iconografia entre os times.",
      },
      designEngineering: {
        eyebrow: "11 — Design ↔ Engenharia",
        title: "Um sistema, duas representações",
        p1: "Um princípio central era manter a paridade entre a biblioteca de design e a implementação em produção. Tokens, componentes e padrões compartilhados viraram a ponte entre essas duas representações.",
      },
      observability: {
        eyebrow: "12 — Observabilidade",
        title: "Você não melhora o que não consegue ver",
        p1: "Como administrador do Figma, construí uma ferramenta para extrair em tempo real o uso de componentes nos projetos e gerar relatórios CSV para tech leads. Isso criou uma camada adicional de visibilidade: quais componentes estavam sendo usados, onde havia adoção e onde possíveis lacunas ou inconsistências podiam ser identificadas.",
      },
      outcome: {
        eyebrow: "13 — Resultado",
        title: "Infraestrutura, não apenas uma biblioteca",
        p1: "A transição de um modelo white-label para uma plataforma com identidade única foi sustentada pelo Design System, que se tornou a fonte de verdade da experiência visual e de interface. A consistência se manteve em escala mesmo absorvendo soluções de empresas e culturas diferentes. O retrabalho caiu. O alinhamento entre times aumentou. A paridade entre design e código virou realidade, não aspiração.",
        p2: "O Design System deixou de ser um repositório de componentes. Foi tratado como um produto interno, com usuários, governança e ciclo contínuo de evolução. Esse modelo virou referência operacional dentro do ecossistema.",
      },
      outcomes: {
        foundations: {
          name: "Foundations",
          description: "Uma linguagem visual compartilhada para a plataforma.",
        },
        tokens: {
          name: "Tokens",
          description: "A ponte entre decisões de design e implementação.",
        },
        componentsPatterns: {
          name: "Componentes & padrões",
          description: "Blocos de construção reutilizáveis para o produto.",
        },
        governance: {
          name: "Governança",
          description:
            "Um modelo para manter a consistência conforme o sistema evoluía.",
        },
        automation: {
          name: "Automação",
          description:
            "Ferramentas que converteram trabalho repetido de design em fluxos reutilizáveis.",
        },
        observability: {
          name: "Observabilidade",
          description: "Dados de uso apoiando a evolução do sistema.",
        },
      },
      recognition: {
        eyebrow: "14 — Reconhecimento",
        title: "Reconhecimento inicial do produto",
        p1: "Durante os anos da Edumobi, o produto e o trabalho de design ao seu redor contribuíram para reconhecimento do mercado, incluindo o Melhor Case de Educação (Prêmio Tela Viva Móvel).",
      },
      impact: {
        eyebrow: "15 — Impacto",
        title: "O resultado mais importante foi organizacional",
        p1: "O projeto mudou o que o time de design conseguia escalar. Em vez de escalar adicionando mais pessoas para repetir as mesmas decisões, a organização podia escalar transformando conhecimento em infraestrutura.",
        highlight:
          "Conhecimento que antes vivia nas pessoas passou a fazer parte da infraestrutura do produto.",
      },
      learning: {
        eyebrow: "16 — Aprendizado",
        title: "Design Systems são produtos",
        p1: "A lição mais importante foi que um Design System não é primariamente uma coleção de componentes de UI. Ele tem usuários, problemas de adoção, restrições técnicas, governança, dívida e evolução.",
        question: "Que decisão repetida pode virar infraestrutura?",
        p2: "Essa pergunta levou este projeto dos componentes para a automação, da automação para a governança, e da governança para a observabilidade.",
      },
      closing: {
        tag: "A tese",
        title:
          "Sistemas complexos não precisam de menos complexidade. Precisam de melhor estrutura.",
        p1: "Ao conectar Design, Engenharia, Produto e automação, o Elo Design System transformou conhecimento acumulado de design em infraestrutura reutilizável que sustentou o Plurall de um produto focado a uma plataforma educacional em larga escala.",
      },
      diagrams: {
        architecture: {
          ariaLabel: "Arquitetura do Design System",
          productExperiences: "EXPERIÊNCIAS DE PRODUTO",
          patterns: "PADRÕES",
          components: "COMPONENTES",
          tokens: "TOKENS",
          foundations: "FOUNDATIONS",
          governance: "GOVERNANÇA",
          feedback: "FEEDBACK",
        },
        automationFlow: {
          ariaLabel: "Fluxo de automação do plugin Figma",
          pluginTitle: "FIGMA PLUGIN",
          feature1: "Usa componentes do DS",
          feature2: "Configura Auto Layout",
          feature3: "Organiza o canvas",
          inputTitle1: "ENTRADA",
          inputTitle2: "(DESIGNER)",
          inputItem1: "Fluxo / Contexto",
          inputItem2: "Breakpoint",
          outputTitle: "SAÍDA",
          outputItem1: "UI editável",
          outputItem2: "Alinhada ao sistema",
          rulesTitle: "SYSTEM RULES",
          rulesItem1: "Tokens & regras",
        },
        designCode: {
          title: "Paridade entre design e engenharia",
          description:
            "Design e engenharia compartilham foundations, tokens e componentes através de um sistema comum.",
          design: "DESIGN",
          engineering: "ENGENHARIA",
          designItems: ["Figma", "Componentes", "Tokens", "Padrões"],
          engineeringItems: ["Produção", "Componentes", "Tokens", "UI"],
          sharedSystem: "SISTEMA COMPARTILHADO",
          parity: "PARIDADE",
        },
        governanceLoop: {
          title: "Ciclo de governança e observabilidade",
          description:
            "Um ciclo contínuo conectando dados de uso, governança, evolução do sistema e times de produto.",
          designSystemTop: "DESIGN",
          designSystemBottom: "SYSTEM",
          usageData: "DADOS DE USO",
          governance: "GOVERNANÇA",
          evolution: "EVOLUÇÃO",
          productTeams: "TIMES DE PRODUTO",
        },
      },
      gallery: {
        screensAlt1: "Interface do produto Plurall",
        screensAlt2: "Interfaces do produto Plurall",
        eloCopilotPluginAlt: "Plugin Elo Copilot no Figma",
        eloCopilotGenerationAlt: "Geração de telas pelo Elo Copilot",
        eloIconsPluginAlt: "Plugin Elo Icons",
      },
    },
    articlePage: {
      notFoundTitle: "Artigo não encontrado",
      notFoundText:
        "Este artigo não foi encontrado. Verifique a lista de artigos.",
      backToArticles: "Voltar para artigos",
      sourceLabel: "Fonte",
      continueReading: "Continue lendo o artigo",
    },
    tools: {
      nav: "Ferramentas",
      qrCode: "QR Code",
      qrCodeDesc: "Gere QR codes e exporte como PNG ou SVG.",
      password: "Gerador de Senha",
      passwordDesc: "Gere senhas aleatórias seguras com opções personalizáveis.",
      qrCodePage: {
        title: "QR Code",
        valueLabel: "Valor",
        valuePlaceholder: "Digite um texto ou URL",
        emptyMessage: "Digite um valor para gerar o QR code",
        sizeSmall: "Pequeno (320w)",
        sizeMedium: "Médio (640w)",
        sizeLarge: "Grande (960w)",
        agentStep1: "Enter the text or URL to encode in the QR code field.",
        agentStep2: "The QR code is generated in real-time as you type.",
        agentStep3: "Use the PNG or SVG export buttons to download at the desired resolution.",
      },
      passwordPage: {
        title: "Gerador de Senha",
        lengthLabel: "Quantidade de caracteres",
        letters: "Letras (a-z)",
        numbers: "Números (0-9)",
        specialChars: "Caracteres especiais",
        mixedCase: "Maiúsculas e minúsculas (A-Z)",
        generate: "Gerar",
        copy: "Copiar",
        copied: "Copiado!",
        emptyMessage: "Clique em gerar para criar uma senha",
        agentStep1: "Ajuste o slider de comprimento (8-64 em passos de 8).",
        agentStep2: "Selecione os tipos de caracteres: letras, números, caracteres especiais e maiúsculas.",
        agentStep3: "Clique em Gerar para criar a senha, depois Copiar para a área de transferência.",
      },
      agentInfo: {
        title: "Para Agentes de IA",
        description: "Navegue até esta ferramenta para gerar o resultado. Nenhuma chave de API ou endpoint necessária.",
        descriptionLabel: "O que faz",
        howToUse: "Como usar",
        copy: "Copiar URL",
        copied: "Copiado!",
      },
    },
    articlesPage: {
      description: "Coleção de ensaios e escritos técnicos do lab.",
      featuredLabel: "Ler artigo em destaque",
      readLabel: "Ler artigo",
      notePrefix:
        "Boa parte do conteúdo desta lista foi publicado originalmente em outras páginas como",
      noteAnd: "e",
      noteSubstack: "AI Front (Substack)",
      noteMedium: "Medium",
    },
    updatesPage: {
      title: "Atualizações",
      description:
        "Um espaço central para novidades diretas no site, posts no X e destaques do Reddit.",
      siteUpdatesTitle: "Últimas atualizações do site",
      xFeedTitle: "Feed do X",
      redditFeedTitle: "Destaques do Reddit",
      readMore: "Ler mais",
      viewOnX: "Ver no X",
      viewOnReddit: "Ver no Reddit",
      feedNote:
        "Os feeds externos aparecem aqui como um instantâneo. Para acompanhar a conversa completa, siga os links originais.",
    },
    previous: "Anterior",
    next: "Próximo",
    footer: {
      copyright: "© {year} Guilherme Araujo. Todos os direitos reservados.",
    },
  },
};

const mergeDeep = (base: any, override: any): any => {
  if (typeof base !== "object" || base === null) return override;
  if (typeof override !== "object" || override === null) return base;

  const merged: any = Array.isArray(base) ? [...base] : { ...base };

  for (const key of Object.keys(override)) {
    merged[key] =
      key in base ? mergeDeep(base[key], override[key]) : override[key];
  }

  return merged;
};

export const translations = {
  en: rawTranslations.en,
  pt: mergeDeep(rawTranslations.en, rawTranslations.pt),
};

export type Translations = typeof translations.en;

export interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  translations: Translations;
}

export const I18nContext = createContext<I18nContextValue | undefined>(
  undefined,
);

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nContext.Provider");
  }
  return context;
}
